import { useEffect, useState, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ClipboardList,
  Flag,
  Gauge,
  Map,
  Sparkles,
  TestTube2,
} from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { useAuth } from '../lib/auth'

type Dashboard = {
  thesis?: string
  idea_count?: number
  delivery_count?: number
  roadmap_count?: number
  at_risk_count?: number
  ideas_by_status?: Record<string, number>
  top_ideas?: {
    idea_id: string
    title: string
    status?: string
    rice?: number
  }[]
  deliveries?: {
    delivery_id: string
    idea_id: string
    status?: string
    qa_status?: string
    percent_complete?: number
    timeline?: { target_end?: string | null }
  }[]
  qa?: Record<string, number>
  matrix?: {
    cells?: Record<string, { idea_id: string; title: string; rice?: number }[]>
  }
  jpd_parity?: Record<string, string>
}

type Cycles = {
  overall_score?: number
  cycles?: {
    cycle: number
    name: string
    score_after: number
    shipped: string[]
  }[]
  evolution?: string
  jpd_parity_counts?: Record<string, number>
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

export function ProductPage() {
  const { user, loading } = useAuth()
  const [dash, setDash] = useState<Dashboard | null>(null)
  const [cycles, setCycles] = useState<Cycles | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [title, setTitle] = useState('')

  async function load() {
    setBusy(true)
    setError(null)
    try {
      const [d, c] = await Promise.all([
        api<Dashboard>('/api/afos360/v1/product'),
        api<Cycles>('/api/afos360/v1/product/cycles'),
      ])
      setDash(d)
      setCycles(c)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load product dashboard')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    if (user) void load()
  }, [user])

  if (!loading && !user) return <Navigate to="/signin" replace />

  async function seed() {
    setBusy(true)
    try {
      await api('/api/afos360/v1/product/seed-demo', { method: 'POST' })
      setMessage('Seeded demo product lifecycle (ideas, LOE, roadmap, Jira draft).')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Seed failed')
    } finally {
      setBusy(false)
    }
  }

  async function createIdea() {
    if (!title.trim()) return
    setBusy(true)
    try {
      const idea = await api<{ idea_id: string }>('/api/afos360/v1/product/ideas', {
        method: 'POST',
        body: JSON.stringify({ title, impact: 3, effort: 2, confidence: 0.8 }),
      })
      await api(`/api/afos360/v1/product/ideas/${encodeURIComponent(idea.idea_id)}/loe`, {
        method: 'POST',
      })
      await api(
        `/api/afos360/v1/product/ideas/${encodeURIComponent(idea.idea_id)}/delivery`,
        { method: 'POST' },
      )
      setTitle('')
      setMessage(`Created idea ${idea.idea_id} with AI LOE + delivery card.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed')
    } finally {
      setBusy(false)
    }
  }

  const matrix = dash?.matrix?.cells || {}

  return (
    <AppChrome>
      <main className="afos-page space-y-8">
        <header>
          <h1 className="afos-page-title">Product lifecycle</h1>
          <p className="afos-page-lead">
            Product managers, engineering, and QA on one surface — ideas, RICE, AI team LOE,
            roadmaps, Jira/Linear delivery, and testing status.
          </p>
        </header>

        {dash?.thesis && (
          <div className="glass rounded-3xl p-5 text-sm text-[var(--color-mist)]">
            {dash.thesis}
          </div>
        )}

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={<ClipboardList className="h-5 w-5 text-[var(--color-cyan)]" />} label="Ideas" value={String(dash?.idea_count ?? '—')} />
          <Stat icon={<Gauge className="h-5 w-5 text-[var(--color-amber)]" />} label="In delivery" value={String(dash?.delivery_count ?? '—')} />
          <Stat icon={<Map className="h-5 w-5 text-[var(--color-cyan)]" />} label="Roadmaps" value={String(dash?.roadmap_count ?? '—')} />
          <Stat icon={<Flag className="h-5 w-5 text-[var(--color-rose)]" />} label="At risk" value={String(dash?.at_risk_count ?? '—')} />
        </section>

        <div className="flex flex-wrap items-end gap-2">
          <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-xs text-[var(--color-mist)]">
            New idea
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Capture opportunity or problem…"
              className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-3 py-2 text-sm text-[var(--color-foam)]"
            />
          </label>
          <button
            type="button"
            disabled={busy || !title.trim()}
            onClick={() => void createIdea()}
            className="rounded-full bg-[var(--color-cyan)] px-4 py-2 text-sm font-semibold text-[var(--color-void)]"
          >
            Capture + LOE
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void seed()}
            className="rounded-full border border-[var(--color-line)] px-4 py-2 text-sm text-[var(--color-foam)]"
          >
            Seed demo board
          </button>
        </div>

        {error && (
          <div className="rounded-2xl border border-[rgba(255,107,138,0.4)] bg-[rgba(255,107,138,0.1)] px-4 py-3 text-sm text-[var(--color-rose)]">
            {error}
          </div>
        )}
        {message && (
          <div className="rounded-2xl border border-[rgba(46,230,197,0.35)] bg-[rgba(46,230,197,0.08)] px-4 py-3 text-sm text-[var(--color-cyan)]">
            {message}
          </div>
        )}

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-3">
            <h2 className="font-[family-name:var(--font-display)] text-2xl font-extrabold">
              Priority backlog
            </h2>
            {(dash?.top_ideas || []).map((idea, i) => (
              <motion.div
                key={idea.idea_id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="glass rounded-3xl p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-[var(--color-foam)]">{idea.title}</div>
                    <div className="mt-1 font-[family-name:var(--font-mono)] text-[10px] text-[var(--color-mist)]">
                      {idea.idea_id} · {idea.status}
                    </div>
                  </div>
                  <span className="rounded-full bg-white/5 px-3 py-1 text-[10px] uppercase tracking-wider text-[var(--color-cyan)]">
                    RICE {idea.rice ?? '—'}
                  </span>
                </div>
              </motion.div>
            ))}
            {!dash?.top_ideas?.length && (
              <p className="text-sm text-[var(--color-mist)]">
                No ideas yet. Capture one or seed the demo board.
              </p>
            )}

            <h2 className="pt-4 font-[family-name:var(--font-display)] text-2xl font-extrabold">
              Impact × effort
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {(['quick_wins', 'big_bets', 'fill_ins', 'money_pits'] as const).map((key) => (
                <div key={key} className="rounded-2xl border border-[var(--color-line)] bg-black/25 p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--color-mist)]">
                    {key.replace('_', ' ')}
                  </div>
                  <ul className="mt-2 space-y-1 text-xs text-[var(--color-foam)]">
                    {(matrix[key] || []).slice(0, 4).map((row) => (
                      <li key={row.idea_id}>{row.title}</li>
                    ))}
                    {!(matrix[key] || []).length && (
                      <li className="text-[var(--color-mist)]">—</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="font-[family-name:var(--font-display)] text-2xl font-extrabold">
              Delivery & QA
            </h2>
            <div className="flex flex-wrap gap-2 text-xs text-[var(--color-mist)]">
              <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-line)] px-3 py-1">
                <TestTube2 className="h-3.5 w-3.5" /> QA passed {dash?.qa?.passed ?? 0}
              </span>
              <span className="rounded-full border border-[var(--color-line)] px-3 py-1">
                blocked {dash?.qa?.blocked ?? 0}
              </span>
              <span className="rounded-full border border-[var(--color-line)] px-3 py-1">
                in progress {dash?.qa?.in_progress ?? 0}
              </span>
            </div>
            <ul className="space-y-2">
              {(dash?.deliveries || []).map((d) => (
                <li
                  key={d.delivery_id}
                  className="rounded-2xl border border-[var(--color-line)] bg-black/25 px-4 py-3"
                >
                  <div className="flex justify-between gap-3 text-sm text-[var(--color-foam)]">
                    <span>{d.idea_id}</span>
                    <span>{d.percent_complete ?? 0}%</span>
                  </div>
                  <div className="mt-1 text-xs text-[var(--color-mist)]">
                    {d.status} · QA {d.qa_status}
                    {d.timeline?.target_end ? ` · target ${d.timeline.target_end.slice(0, 10)}` : ''}
                  </div>
                </li>
              ))}
              {!dash?.deliveries?.length && (
                <p className="text-sm text-[var(--color-mist)]">No delivery cards yet.</p>
              )}
            </ul>

            <h2 className="pt-2 font-[family-name:var(--font-display)] text-2xl font-extrabold">
              JPD parity & improve cycles
            </h2>
            <p className="text-sm text-[var(--color-mist)]">
              Overall score {cycles?.overall_score ?? '—'} / 5 — {cycles?.evolution}
            </p>
            <ul className="space-y-2">
              {(cycles?.cycles || []).map((c) => (
                <li key={c.cycle} className="glass rounded-3xl p-4">
                  <div className="flex items-center gap-2 text-[var(--color-cyan)]">
                    <Sparkles className="h-4 w-4" />
                    <span className="text-xs font-semibold uppercase tracking-[0.16em]">
                      Cycle {c.cycle} · {c.score_after}/5
                    </span>
                  </div>
                  <div className="mt-1 font-semibold text-[var(--color-foam)]">{c.name}</div>
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-[var(--color-mist)]">
                    {(c.shipped || []).slice(0, 3).map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
            {dash?.jpd_parity && (
              <div className="rounded-2xl border border-[var(--color-line)] bg-black/25 p-4 text-[10px] text-[var(--color-mist)]">
                {Object.entries(dash.jpd_parity).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 border-b border-[var(--color-line)] py-1 last:border-0">
                    <span>{k}</span>
                    <span className="text-[var(--color-cyan)]">{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </AppChrome>
  )
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: ReactNode
  label: string
  value: string
}) {
  return (
    <div className="glass rounded-3xl p-5">
      <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[var(--color-mist)]">
        {icon}
        {label}
      </div>
      <div className="font-[family-name:var(--font-display)] text-3xl font-extrabold text-[var(--color-foam)]">
        {value}
      </div>
    </div>
  )
}
